import React, { useState } from 'react';
import AlignmentRadioGroup from 'components/AlignmentRadioGroup/AlignmentRadioGroup';
import { Tag } from 'components/RadioTag/RadioTag';
import { parseJustifyContentToAlignment, parseAlignmentToJustifyContent } from 'utils/alignmentParsers';
import { AzulCheckbox } from '@the-ksquare-group/azul-ds';
import { Box, Divider, Grid, SelectChangeEvent, Typography } from '@mui/material';
import {
  addElementIdToRecipient,
  removeElementIdFromRecipients,
  selectedElement,
  updateContentElementById,
  selectRecipients,
} from 'features/document/documentSlice';
import { isSignatureElement } from 'utils/typeGuards';
import RecipientSelect from 'components/RecipientSelect/RecipientSelect';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { colorPalette } from 'styles/partials/colors';

interface SignatureSidebarProps {
  currentElement: selectedElement;
}

export default function SignatureSidebar({ currentElement }: SignatureSidebarProps) {
  if (!isSignatureElement(currentElement.data)) {
    return null;
  }

  const dispatch = useAppDispatch();

  const {
    data: { recipient: currentRecipient },
  } = currentElement;

  const recipients = useAppSelector(selectRecipients);

  const { required } = currentElement.data.content;
  const parsedJustifyContent = parseJustifyContentToAlignment(currentElement.data.style.justifyContent);
  const [alignment, setAlignment] = useState<Tag>({ label: parsedJustifyContent, value: parsedJustifyContent });

  const handleOnChangeAlignment = (newAlignment: Tag) => {
    setAlignment(newAlignment);

    dispatch(
      updateContentElementById({
        style: {
          justifyContent: parseAlignmentToJustifyContent(newAlignment.value),
        },
      })
    );
  };

  const handleOnChangeRequired = (newRequired: boolean) => {
    dispatch(
      updateContentElementById({
        content: {
          required: newRequired,
        },
      })
    );
  };

  const handleOnChangeRecipient = (event: SelectChangeEvent) => {
    dispatch(removeElementIdFromRecipients(currentElement.data.id));

    const selectedRecipient = recipients.find(recipient => recipient.fullName === event.target.value);
    if (selectedRecipient) {
      dispatch(addElementIdToRecipient({ recipientId: selectedRecipient.id, elementId: currentElement.data.id }));

      dispatch(
        updateContentElementById({
          content: {
            labelText: `${selectedRecipient.fullName} Signature.`,
          },
          style: {},
          recipient: selectedRecipient,
        })
      );
    }
  };

  return (
    <Grid container direction='row' className='container' gap={2}>
      <Grid item xs={12}>
        <Typography color={colorPalette.darkGray1} fontSize={'16px'} fontWeight={500} marginBottom={'32px'}>
          Signature Block
        </Typography>
        <Box display={'flex'} flexDirection={'column'} gap={'24px'}>
          <Typography color={colorPalette.darkGray1} fontSize={'14px'} fontWeight={500}>
            Recipient
          </Typography>
          <RecipientSelect
            value={currentRecipient}
            recipientOptions={recipients}
            editRecipients={false}
            onChange={handleOnChangeRecipient}
          />
        </Box>
        <Divider
          color='#C9CACB'
          flexItem
          orientation='horizontal'
          variant='middle'
          sx={{ margin: '24px 0', width: '100%' }}
        />
        <Box color={colorPalette.darkGray1}>
          <AzulCheckbox checked={required} label='Required' onChange={e => handleOnChangeRequired(e.target.checked)} />
        </Box>
        <Typography
          color={colorPalette.darkGray1}
          fontSize={'14px'}
          fontWeight={500}
          marginTop={'32px'}
          marginBottom={'24px'}
        >
          Alignment
        </Typography>
        <Box maxWidth={'210px'}>
          <AlignmentRadioGroup selected={alignment} handleChange={handleOnChangeAlignment} />
        </Box>
      </Grid>
    </Grid>
  );
}
